export const IOS_FULLSCREEN_FALLBACK_CLASS = 'simulated-fullscreen';
export const IOS_FULLSCREEN_BODY_CLASS = 'simulated-fullscreen-active';

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

export function nativeFullscreenElement(documentRef: Document = document): Element | null {
  const fullscreenDocument = documentRef as FullscreenDocument;
  return documentRef.fullscreenElement || fullscreenDocument.webkitFullscreenElement || null;
}

export function isFallbackFullscreen(element: HTMLElement | null | undefined): boolean {
  return Boolean(element?.classList.contains(IOS_FULLSCREEN_FALLBACK_CLASS));
}

export function isElementFullscreen(
  element: HTMLElement | null | undefined,
  documentRef: Document = document,
): boolean {
  return Boolean(element && (nativeFullscreenElement(documentRef) === element || isFallbackFullscreen(element)));
}

export async function requestNativeFullscreen(
  element: HTMLElement,
  documentRef: Document = document,
): Promise<boolean> {
  const fullscreenElement = element as FullscreenElement;
  const request = element.requestFullscreen || fullscreenElement.webkitRequestFullscreen;
  if (typeof request !== 'function') {
    return false;
  }
  try {
    await Promise.resolve(request.call(element));
    // iPhone Safari can expose a WebKit fullscreen method for a non-video
    // element and resolve the call without actually entering fullscreen. Only
    // report success when the document confirms that this element owns it.
    return nativeFullscreenElement(documentRef) === element;
  } catch {
    return false;
  }
}

export async function exitNativeFullscreen(documentRef: Document = document): Promise<boolean> {
  const fullscreenDocument = documentRef as FullscreenDocument;
  const exit = documentRef.exitFullscreen || fullscreenDocument.webkitExitFullscreen;
  if (typeof exit !== 'function') {
    return false;
  }
  try {
    await Promise.resolve(exit.call(documentRef));
    return true;
  } catch {
    return false;
  }
}

export function setFallbackFullscreen(
  element: HTMLElement,
  active: boolean,
  documentRef: Document = document,
): void {
  element.classList.toggle(IOS_FULLSCREEN_FALLBACK_CLASS, active);
  documentRef.documentElement?.classList.toggle(IOS_FULLSCREEN_BODY_CLASS, active);
  documentRef.body?.classList.toggle(IOS_FULLSCREEN_BODY_CLASS, active);
}
